import { createFileRoute } from "@tanstack/react-router";
import { Sparkles, Languages, Target, Users, Send, LineChart, Bookmark } from "lucide-react";
import { AiGeneratorPage } from "@/components/marketing/AiGeneratorPage";

export const Route = createFileRoute("/ai/email-writer")({
  head: () => ({
    meta: [
      { title: "Free AI Email Writer — Cold Emails, Follow-ups & Outreach in Seconds | banana.cards" },
      { name: "description", content: "Write cold emails, sales follow-ups and outreach that gets replies. Free AI email generator — pick tone, language and CTA, get a personalized draft in seconds." },
      { property: "og:title", content: "Free AI Email Writer — banana.cards" },
      { property: "og:description", content: "Cold emails, follow-ups and outreach in seconds. Free AI email writer — personalized, on-tone, ready to send." },
      { property: "og:url", content: "https://banana.cards/ai/email-writer" },
    ],
    links: [{ rel: "canonical", href: "https://banana.cards/ai/email-writer" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "AI Email Writer",
          applicationCategory: "BusinessApplication",
          operatingSystem: "Web",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
        }),
      },
    ],
  }),
  component: Page,
});

const EXAMPLES = [
  "Cold email to a SaaS founder pitching my UX audit service, friendly tone, CTA = 15 min call",
  "Follow-up email to a prospect who didn't reply after our demo last week",
  "Re-engagement email to a customer who hasn't logged in for 60 days",
  "Networking email to someone I met at a conference yesterday — short and warm",
  "Polite chase email about an unpaid invoice that's 14 days overdue",
  "Welcome email for a new newsletter subscriber, brand voice = playful",
];

export function Page() {
  return (
    <AiGeneratorPage
      eyebrow="AI Email Writer"
      title="The right email,"
      italic="every time."
      description="Cold outreach, follow-ups, welcome emails, chase emails — Banana drafts in your tone, in 50+ languages, with a clear CTA and a subject line that actually gets opened."
      headline="What kind of email would you like to write?"
      placeholders={EXAMPLES}
      linkedFromHref="/platform/networking/welcome-email"
      linkedFromLabel="Banana — Email & Outreach"
      sections={[
        { icon: Sparkles, title: "On-tone, on-brand", desc: "Banana learns your voice from prior emails so drafts sound like you, not a template." },
        { icon: Target, title: "Built around a CTA", desc: "Every draft is engineered around a single action — book a call, reply, click, pay." },
        { icon: Languages, title: "50+ languages", desc: "Write or translate to Spanish, French, German, Italian, Mandarin, Japanese and more." },
      ]}
      capabilities={[
        "Cold outreach, follow-up, welcome, chase, re-engagement",
        "Tone presets (friendly, formal, playful, direct)",
        "Subject-line variants (A/B test ready)",
        "Personalization from contact + company data",
        "Variables like {{first_name}} and {{company}}",
        "Auto-translate to 50+ languages",
        "Send straight from Gmail or Outlook",
        "Track opens, clicks and replies",
      ]}
      howItWorks={[
        { title: "Describe the email", desc: "Audience, goal, tone — one sentence is enough." },
        { title: "AI drafts subject + body", desc: "Multiple subject-line variants plus a clean draft." },
        { title: "Tweak in one click", desc: "Shorter, friendlier, more urgent — Banana rewrites instantly." },
        { title: "Send & track", desc: "Send from your own inbox; we track opens, clicks and replies." },
      ]}
      faq={[
        { q: "Will the email actually sound like me, not a robot?", a: "Yes — Banana trains a workspace-wide voice on your prior emails (tone, sentence length, sign-off, phrases you use). Individual teammates can layer their own personal voice on top. The result is drafts that sound like you wrote them, not a generic AI." },
        { q: "Does the AI write subject lines that get opened?", a: "Yes — every draft comes with 3 subject-line variants optimized for open rate, plus a preview snippet. You can A/B test them automatically when sending sequences." },
        { q: "Can I send the email straight from my own inbox?", a: "Yes — connect Gmail or Outlook and Banana sends through your real address (not a relay), so replies land in your normal inbox and your sender domain reputation isn't affected. SMTP and Sendgrid/Resend/Postmark are also supported on paid plans." },
        { q: "Does it work in languages other than English?", a: "Yes — drafting and translation work in 50+ languages including Spanish, French, German, Italian, Portuguese, Dutch, Mandarin, Japanese, Korean, Vietnamese, Thai, Arabic and Hindi. Write your prompt in any of those and you'll get a native-quality draft back." },
        { q: "Is the free generator above limited?", a: "It's a full preview — you can run unlimited prompts in the browser. Saving drafts, sending from your inbox, A/B testing, tracking opens/clicks and bulk personalization require a free Banana account." },
      ]}
      flow={{
        steps: [
          {
            question: "What kind of email?",
            multi: false,
            options: ["Cold outreach", "Follow-up", "Introduction", "Meeting request", "Proposal", "Re-engagement", "Chase invoice", "Thank you"],
          },
          {
            question: "What tone?",
            multi: false,
            options: ["Friendly", "Professional", "Bold", "Concise", "Persuasive", "Playful"],
          },
          {
            question: "How long should it be?",
            multi: false,
            options: ["Short (3 lines)", "Medium (1 paragraph)", "Long (multi-paragraph)"],
          },
        ],
        loadingMessages: [
          "Analyzing your intent…",
          "Researching best-performing angles…",
          "Drafting subject line variants…",
          "Writing the opener…",
          "Tightening the CTA…",
          "A/B checking tone…",
          "Polishing the sign-off…",
        ],
        readyTitle: "Your email is ready.",
        readyBody: "Banana AI wrote a personalized email plus 3 subject line variants. Sign up to view, personalize per lead and send it from your own inbox.",
        ctaLabel: "Sign up to view it",
        ctaHref: "https://app.banana.cards/signup",
        nextThings: [
          { icon: Users, title: "Personalize per contact", desc: "Auto-fill first name, company, industry from your CRM." },
          { icon: Send, title: "Send from your domain", desc: "Gmail, Outlook, SMTP — your reputation, your inbox." },
          { icon: LineChart, title: "Track opens & replies", desc: "See who opened, clicked and replied in real time." },
          { icon: Bookmark, title: "Save as a template", desc: "Reuse this exact draft across future campaigns." },
        ],
      }}
    />
  );
}
